import './creditsScreen.css';
import { createPlateLayer } from './plateLayer.js';
import { getSettings, settingsStore } from '../settings/store.js';

interface CreditsLine {
  kind: 'text' | 'bullet' | 'subhead';
  text: string;
}

interface CreditsSection {
  title: string;
  lines: CreditsLine[];
}

const FRAMING_SECTIONS: readonly CreditsSection[] = [
  {
    title: 'In Memoriam',
    lines: [
      {
        kind: 'text',
        text: 'This register keeps the makers, archives, and public works that gave the record its surfaces.',
      },
      {
        kind: 'text',
        text: 'Nothing here asks for applause. The names and sources are kept plainly, as they were used.',
      },
    ],
  },
  {
    title: 'Return',
    lines: [
      { kind: 'text', text: 'The roll closes here. Return when ready.' },
    ],
  },
];

const FALLBACK_SECTION: CreditsSection = {
  title: 'Archive Sources',
  lines: [{ kind: 'text', text: 'The credits register could not be read from /assets/CREDITS.md.' }],
};

let creditsSectionsPromise: Promise<CreditsSection[]> | null = null;

const esc = (value: string): string =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const makeSection = (title: string): CreditsSection => ({ title, lines: [] });

const parseCredits = (markdown: string): CreditsSection[] => {
  const sections: CreditsSection[] = [];
  let current = makeSection('Archive Sources');
  for (const raw of markdown.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('# ')) continue;
    if (line.startsWith('## ')) {
      if (current.lines.length) sections.push(current);
      current = makeSection(line.slice(3).trim());
      continue;
    }
    if (line.startsWith('### ')) current.lines.push({ kind: 'subhead', text: line.slice(4).trim() });
    else if (line.startsWith('- ')) current.lines.push({ kind: 'bullet', text: line.slice(2).trim() });
    else if (raw.startsWith('  ') && current.lines.length > 0) current.lines.at(-1)!.text += ` ${line}`;
    else current.lines.push({ kind: 'text', text: line });
  }
  if (current.lines.length) sections.push(current);
  return sections;
};

const withFraming = (sections: CreditsSection[]): CreditsSection[] => [
  FRAMING_SECTIONS[0]!,
  ...sections,
  FRAMING_SECTIONS[1]!,
];

const loadCreditsSections = (): Promise<CreditsSection[]> => {
  if (!creditsSectionsPromise) {
    creditsSectionsPromise = fetch('/assets/CREDITS.md')
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((markdown) => withFraming(parseCredits(markdown)))
      .catch(() => withFraming([FALLBACK_SECTION]));
  }
  return creditsSectionsPromise;
};

const renderLine = (line: CreditsLine): string => {
  if (line.kind === 'subhead') return `<div class="cs-subhead">${esc(line.text)}</div>`;
  const cls = line.kind === 'bullet' ? 'cs-line cs-bullet' : 'cs-line';
  return `<p class="${cls}">${esc(line.text)}</p>`;
};

const renderSections = (sections: CreditsSection[]): string =>
  sections
    .map(
      (section) => `
        <section class="cs-section">
          <h2 class="cs-title">${esc(section.title)}</h2>
          <div class="cs-copy">${section.lines.map(renderLine).join('')}</div>
        </section>`,
    )
    .join('');

export class CreditsScreen {
  private readonly root: HTMLDivElement;
  private readonly roll: HTMLDivElement;
  private readonly content: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private open = false;
  private lastTick = 0;
  private raf = 0;
  private onClose: (() => void) | null = null;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'credits-screen';
    this.root.innerHTML = `
      <div class="cs-shell">
        <div class="cs-shade" aria-hidden="true"></div>
        <div class="cs-head">
          <div class="cs-folio">Cenotaph Obsidian · Memorial Roll</div>
          <button class="cs-close" data-action="close">Return</button>
        </div>
        <div class="cs-roll">
          <div class="cs-content">
            <p class="cs-loading">Loading the register.</p>
          </div>
        </div>
        <div class="cs-status">Auto rolling · Return / Esc</div>
      </div>`;
    this.root.prepend(
      createPlateLayer({
        id: 'bm-credits-plate',
        className: 'cs-plate',
        src: '/assets/ui/plate-credits.png',
      }),
    );
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.roll = this.root.querySelector('.cs-roll') as HTMLDivElement;
    this.content = this.root.querySelector('.cs-content') as HTMLDivElement;
    this.status = this.root.querySelector('.cs-status') as HTMLDivElement;
    this.root.addEventListener('click', (event) => this.onClick(event));
    window.addEventListener('keydown', (event) => this.onKey(event));
    this.unsubscribe = settingsStore.subscribe(() => this.syncSettings());
    this.syncSettings();
  }

  show(onClose: () => void): void {
    this.open = true;
    this.onClose = onClose;
    this.root.style.display = 'flex';
    this.content.innerHTML = '<p class="cs-loading">Loading the register.</p>';
    this.roll.scrollTop = 0;
    this.syncSettings();
    void this.hydrate();
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.onClose = null;
    this.stopAutoScroll();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.hide();
    this.unsubscribe?.();
    this.root.remove();
  }

  private async hydrate(): Promise<void> {
    const sections = await loadCreditsSections();
    if (!this.open) return;
    this.content.innerHTML = renderSections(sections);
    this.roll.scrollTop = 0;
    this.lastTick = 0;
    this.syncSettings();
  }

  private syncSettings(): void {
    const reduced = getSettings().accessibility.reduceMotion;
    this.root.classList.toggle('reduced', reduced);
    if (!this.open) return;
    this.status.textContent = reduced ? 'Manual scroll · Return / Esc' : 'Auto rolling · Return / Esc';
    if (reduced) this.stopAutoScroll();
    else this.startAutoScroll();
  }

  private startAutoScroll(): void {
    if (this.raf || !this.open || getSettings().accessibility.reduceMotion) return;
    this.raf = window.requestAnimationFrame((time) => this.tick(time));
  }

  private stopAutoScroll(): void {
    if (!this.raf) return;
    window.cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.lastTick = 0;
  }

  private tick(time: number): void {
    if (!this.open || getSettings().accessibility.reduceMotion) {
      this.stopAutoScroll();
      return;
    }
    if (!this.lastTick) this.lastTick = time;
    const max = this.roll.scrollHeight - this.roll.clientHeight;
    const next = Math.min(max, this.roll.scrollTop + ((time - this.lastTick) / 1000) * 18);
    this.lastTick = time;
    this.roll.scrollTop = next;
    if (next >= max) {
      this.stopAutoScroll();
      this.status.textContent = 'Return / Esc';
      return;
    }
    this.raf = window.requestAnimationFrame((frameTime) => this.tick(frameTime));
  }

  private close(): void {
    if (!this.open) return;
    const cb = this.onClose;
    this.hide();
    cb?.();
  }

  private onClick(event: MouseEvent): void {
    const action = (event.target as HTMLElement).closest<HTMLElement>('[data-action]')?.dataset.action;
    if (action === 'close') this.close();
  }

  private onKey(event: KeyboardEvent): void {
    if (!this.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }
}
